import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaPhoneAlt, FaGlobe, FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getCompanyInfo } from '../firebase/db';
import Logo from './Logo';
import './Navbar.css';

const LANGUAGES = [
  { code: 'mr', pillLabel: 'मराठी', label: 'मराठी' },
  { code: 'en', pillLabel: 'EN', label: 'English' },
  { code: 'kn', pillLabel: 'ಕನ್ನಡ', label: 'ಕನ್ನಡ' }
];

const DesktopLanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLangCode = (i18n.language || 'en').substring(0, 2);
  const currentLang = LANGUAGES.find(l => l.code === currentLangCode) || LANGUAGES[1];

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="lang-switcher-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="lang-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change Language"
      >
        <FaGlobe className="lang-icon" />
        <span>{currentLang.label}</span>
        <FaChevronDown className={`lang-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="lang-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                type="button"
                className={`lang-option ${lang.code === currentLangCode ? 'active' : ''}`}
                onClick={() => handleSelect(lang.code)}
              >
                {lang.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [phone, setPhone] = useState('8956403388');
  const location = useLocation();

  const currentLangCode = (i18n.language || 'en').substring(0, 2);

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
  };

  useEffect(() => {
    getCompanyInfo().then(data => {
      if (data && data.phone) {
        const cleaned = data.phone
          .split(',')
          .map(p => p.trim())
          .filter(p => !p.includes('2644484') && !p.includes('0233'))[0];
        if (cleaned) setPhone(cleaned);
      }
    });
  }, []);

  const toggleMenu = () => setIsOpen(prev => !prev);
  const closeMenu = () => setIsOpen(false);

  // Close drawer on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when side drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close drawer on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navLinks = [
    { name: t('navAbout'), path: '/about' },
    { name: t('navProducts'), path: '/products' },
    { name: t('navServices'), path: '/services' },
    { name: t('navBlog'), path: '/blogs' },
    { name: t('navFAQ'), path: '/faq' },
    { name: t('navContact'), path: '/contact' },
  ];

  const formattedPhone = phone.length === 10
    ? `${phone.slice(0, 5)} ${phone.slice(5)}`
    : phone;

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="nav-logo" onClick={closeMenu}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Logo width="170px" height="54px" />
            </motion.div>
          </Link>

          {/* Desktop Menu */}
          <ul className="nav-menu">
            {navLinks.map((link, index) => (
              <motion.li
                key={link.path}
                className="nav-item"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Link
                  to={link.path}
                  className={`nav-links ${location.pathname === link.path ? 'active-link' : ''}`}
                >
                  {link.name}
                </Link>
              </motion.li>
            ))}
            <motion.li
              className="nav-item"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
            >
              <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="nav-call-btn">
                <FaPhoneAlt /> {t('navCallNow')}
              </a>
            </motion.li>
            <motion.li
              className="nav-item"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <DesktopLanguageSwitcher />
            </motion.li>
          </ul>

          {/* Mobile Hamburger Button */}
          <button
            type="button"
            className="menu-icon"
            onClick={toggleMenu}
            aria-label="Open navigation menu"
          >
            <FaBars />
          </button>
        </div>
      </nav>

      {/* Mobile/Tablet Side Panel Drawer (Matching Image 2 Reference) */}
      <AnimatePresence>
        {isOpen && (
          <div className="drawer-portal-wrapper">
            {/* Dark Backdrop Overlay */}
            <motion.div
              className="drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={closeMenu}
            />

            {/* Side Drawer Panel (Sliding in from the Right) */}
            <motion.aside
              className="nav-drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              role="dialog"
              aria-modal="true"
              aria-label="Mobile Navigation"
            >
              {/* 1. Dark Top Header */}
              <div className="drawer-header">
                <div className="drawer-brand">
                  <div className="drawer-logo-badge">
                    <img
                      src={process.env.PUBLIC_URL + '/logo1.jpg'}
                      alt="Digvijay Industries"
                      className="drawer-logo-img"
                    />
                  </div>
                  <div className="drawer-brand-text">
                    <span className="drawer-brand-name">DIGVIJAY</span>
                    <span className="drawer-brand-sub">INDUSTRIES</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="drawer-close-btn"
                  onClick={closeMenu}
                  aria-label="Close menu"
                >
                  <FaTimes />
                </button>
              </div>

              {/* 2. Language Selector Bar with Segmented Pills */}
              <div className="drawer-lang-bar">
                <span className="drawer-lang-label">{t('navLanguage', 'LANGUAGE')}</span>
                <div className="drawer-lang-pills">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`drawer-lang-pill ${currentLangCode === lang.code ? 'active' : ''}`}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      {lang.pillLabel}
                    </button>
                  ))}
                </div>
              </div>

              {/* 3. Left-Aligned Navigation Links */}
              <div className="drawer-body">
                <ul className="drawer-nav-list">
                  {navLinks.map((link) => (
                    <li key={link.path} className="drawer-nav-item">
                      <Link
                        to={link.path}
                        className={`drawer-nav-link ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={closeMenu}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 4. Bottom Action & Contact Info */}
              <div className="drawer-footer">
                <a
                  href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
                  className="drawer-call-btn"
                  onClick={closeMenu}
                >
                  <FaPhoneAlt />
                  <span>{t('navCallNow')}</span>
                </a>
                <a
                  href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
                  className="drawer-contact-line"
                  onClick={closeMenu}
                >
                  <FaPhoneAlt className="drawer-contact-icon" />
                  <span>{t('navContactLabel', 'Contact')}: +91 {formattedPhone}</span>
                </a>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
